import React, { useState } from 'react';
import UIFlex from 'components/ui/UIFlex';
import { Box, Checkbox, IconButton, Popover, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { VocabularyTestWord } from 'app/types';
import { formatVocabularyTestWord } from 'pages/VocabularyTest/utils';
import { STATUS_META } from 'components/dictionaryView/statusMeta';

interface ITestWordProps {
  word: VocabularyTestWord;
  onClick: () => void;
  isSelected: boolean;
}

/**
 * Status accent hue for the chip's left bar + tinted background.
 * NEW/unassigned intentionally has no hue — it must look exactly like an
 * unassigned chip (no fill, no accent), matching the dictionary view.
 *
 * Uses the user's marking (`status`) once they've marked the word this
 * session; otherwise falls back to `existingStatus` so already-known words
 * still show their Vocabulary colour as context (without being "marked").
 */
const pickHue = (word: VocabularyTestWord): string | null => {
  switch (word.status ?? word.existingStatus) {
    case 'ACTIVE':
      return STATUS_META.active.color;
    case 'LEARNED':
      return STATUS_META.learned.color;
    case 'REPEAT':
      return STATUS_META.repeat.color;
    case 'NEW':
    default:
      return null;
  }
};

const TestWord: React.FC<ITestWordProps> = ({ word, onClick, isSelected }) => {
  const displayWord = formatVocabularyTestWord(word);
  const description = word.description?.trim();
  const [infoAnchor, setInfoAnchor] = useState<HTMLElement | null>(null);
  const hue = pickHue(word);

  return (
    <UIFlex sx={{ alignItems: 'center' }}>
      <Box
        onClick={onClick}
        sx={(theme) => {
          const background = isSelected
            ? alpha(theme.palette.primary.main, 0.12)
            : hue
            ? alpha(hue, 0.14)
            : theme.palette.background.paper;
          const borderColor = isSelected
            ? theme.palette.primary.main
            : hue
            ? alpha(hue, 0.5)
            : theme.palette.primary.tableBorder;

          return {
            alignItems: 'center',
            backgroundColor: background,
            border: '1.5px solid',
            borderColor,
            borderRadius: 2,
            cursor: 'pointer',
            display: 'flex',
            gap: 1,
            minHeight: 46,
            pr: 1,
            py: 0.5,
            transition: theme.transitions.create(
              ['background-color', 'border-color'],
              { duration: theme.transitions.duration.shortest },
            ),
            userSelect: 'none',
            width: '100%',
          };
        }}
      >
        <Checkbox
          checked={isSelected}
          onClick={(evt) => {
            evt.stopPropagation();
            onClick();
          }}
          size={'small'}
        />
        <Typography
          sx={{ flex: 1, fontWeight: 600, minWidth: 0, wordBreak: 'break-word' }}
          variant={'subtitle2'}
        >
          {displayWord}
        </Typography>
        {description && (
          <IconButton
            aria-label={'Деталі'}
            onClick={(evt) => {
              evt.stopPropagation();
              setInfoAnchor(evt.currentTarget);
            }}
            size={'small'}
            sx={{
              color: infoAnchor ? 'primary.main' : 'text.secondary',
              flex: 'none',
            }}
          >
            <InfoOutlinedIcon fontSize={'inherit'} />
          </IconButton>
        )}
      </Box>

      {description && (
        <Popover
          anchorEl={infoAnchor}
          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
          disableScrollLock
          onClose={() => setInfoAnchor(null)}
          open={!!infoAnchor}
          slotProps={{ paper: { sx: { maxWidth: 280, p: 2 } } }}
          transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        >
          <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
            {displayWord}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {description}
          </Typography>
        </Popover>
      )}
    </UIFlex>
  );
};

export default TestWord;
